import { useState } from 'react'
import { Images } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { cn } from '@/common/lib/utils'
import { Skeleton } from '@/common/ui/skeleton'
import useQueryGetLuckyWheelImagePresets from '@/modules/promotion/api/hooks/useQueryGetLuckyWheelImagePresets'
import type { LuckyWheelImagePreset } from '@/modules/promotion/type'

interface LuckyWheelImagePresetModalProps {
	type: 'wheel' | 'arrow'
	onSelect: (url: string) => void
}

function PresetImageSkeleton() {
	return (
		<div className="flex flex-col gap-2">
			<Skeleton className="aspect-square w-full rounded-lg" />
			<Skeleton className="h-4 w-3/4" />
		</div>
	)
}

function PresetImageCard({
	preset,
	isSelected,
	onSelect,
}: {
	preset: LuckyWheelImagePreset
	isSelected: boolean
	onSelect: () => void
}) {
	return (
		<button
			type="button"
			onClick={onSelect}
			className={cn(
				'group flex flex-col gap-2 rounded-lg border-2 p-2 text-left transition-all hover:border-primary',
				isSelected ? 'border-primary bg-primary/5' : 'border-border',
			)}
		>
			<div className="overflow-hidden rounded-md bg-muted">
				<img
					src={preset.url}
					alt={preset.name}
					className="aspect-square w-full object-contain transition-transform group-hover:scale-105"
					loading="lazy"
				/>
			</div>
			<p className="text-xs font-medium leading-tight text-foreground">
				{preset.name}
			</p>
		</button>
	)
}

export default function LuckyWheelImagePresetModal({
	type,
	onSelect,
}: LuckyWheelImagePresetModalProps) {
	const [open, setOpen] = useState(false)
	const [selectedUrl, setSelectedUrl] = useState<string | null>(null)

	const { data, isLoading } = useQueryGetLuckyWheelImagePresets(type)
	const presets = data?.presets ?? []

	const handleSelect = (url: string) => {
		setSelectedUrl(url)
	}

	const handleConfirm = () => {
		if (selectedUrl) {
			onSelect(selectedUrl)
		}
		setOpen(false)
		setSelectedUrl(null)
	}

	const handleOpenChange = (value: boolean) => {
		setOpen(value)
		if (!value) setSelectedUrl(null)
	}

	const label = type === 'wheel' ? 'กงล้อ' : 'ลูกศร'

	return (
		<>
			<Button
				type="button"
				variant="outline"
				size="sm"
				onClick={() => setOpen(true)}
				className="gap-1.5"
			>
				<Images className="h-4 w-4" />
				เลือกจากเทมเพลต
			</Button>

			<Dialog open={open} onOpenChange={handleOpenChange}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>เลือกรูปภาพ{label}จากเทมเพลต</DialogTitle>
					</DialogHeader>

					<div className="grid grid-cols-3 gap-3">
						{isLoading
							? Array.from({ length: type === 'wheel' ? 6 : 4 }).map((_, i) => (
									<PresetImageSkeleton key={i} />
								))
							: presets.map((preset) => (
									<PresetImageCard
										key={preset.id}
										preset={preset}
										isSelected={selectedUrl === preset.url}
										onSelect={() => handleSelect(preset.url)}
									/>
								))}
					</div>

					<div className="flex justify-end gap-2 pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => handleOpenChange(false)}
						>
							ยกเลิก
						</Button>
						<Button
							type="button"
							disabled={!selectedUrl}
							onClick={handleConfirm}
						>
							ใช้รูปภาพนี้
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
